/**
 * Canonical plan definitions — the single source of truth for pricing.
 * Consumed by the Landing pricing section and the Pricing page so the two
 * can never drift apart. Internal agent names never appear here; features
 * are described in plain product language.
 */
export interface PlanDef {
  name: string;
  kicker: string;
  price: string;
  per: string;
  tagline: string;
  features: string[];
  cta: string;
  /** Internal route (/signup) or a mailto: for sales-led plans. */
  ctaHref: string;
  featured?: boolean;
}

export const PLANS: PlanDef[] = [
  {
    name: 'Individual',
    kicker: 'For you & your family',
    price: '$399',
    per: '/year',
    tagline:
      'For individuals and families funding scholarships, grants, and fellowships.',
    features: [
      'A personal funding workspace',
      'Scholarships, grants & fellowships matched to you',
      'Eligibility match scores on every program',
      'Application tracker with deadlines',
      'AI essay drafting coach',
      // Batch 3 Stage 3 (all tiers, decision package item 11): the
      // digest line + its documented limits, identical on every plan.
      //
      // RC.22 (owner directive 2026-10-31, superseding the RC.21
      // narrowing) — RESTORE POINT. The RC.21 line ("Saved searches
      // (up to 10) with daily digest") still implied an available
      // digest. Until digest delivery acceptance passes — actual
      // inbox receipt, successful-send recording, no-duplicate
      // rerun, unsubscribe behavior — plan copy must state plainly
      // that email digests are NOT yet enabled, and the digest
      // opt-in controls in SavedSearchesCard render disabled.
      // When (and only when) those four legs pass, restore the
      // original line in this ONE place (and its Growth twin below)
      // AND re-enable the SavedSearchesCard controls:
      //   'Daily digest email for saved searches (up to 10 saved searches)'
      'Saved searches (up to 10) — email digests not yet enabled',
    ],
    cta: 'Get started →',
    ctaHref: '/signup?kind=individual',
  },
  {
    name: 'Growth',
    kicker: 'For emerging teams',
    price: '$2,499',
    per: '/year',
    tagline:
      'For a single nonprofit, university, or small team building a focused funding program.',
    features: [
      'Up to 5 seats',
      'Unlimited opportunity tracking',
      'Fit scoring & evidence briefs',
      'Pipeline & proposal workspace',
      'Proposal readiness and risk flags',
      // R2-BR-D39 (Reading 2, proposal-pending owner approval): basic
      // Analytics + the pipeline CSV report ship on every org plan —
      // the copy now says so instead of implying reporting starts at
      // Professional. Advanced reporting stays Professional/Enterprise
      // wording for when it is built.
      'Pipeline dashboard & CSV report',
      // Batch 3 Stage 3 (all tiers, decision package item 11);
      // narrowed per the RC.22 RESTORE POINT on the Individual plan.
      'Saved searches (up to 10) — email digests not yet enabled',
    ],
    cta: 'Get started →',
    ctaHref: '/signup',
  },
  {
    name: 'Professional',
    kicker: 'Most popular',
    price: '$4,899',
    per: '/year',
    tagline:
      'For active teams managing recurring grants, RFPs, and a shared submission calendar.',
    features: [
      'Up to 20 seats',
      'Everything in Growth',
      'Proposal studio with AI coaching',
      'Funder relationship intelligence',
      'Reporting dashboard & exports',
    ],
    cta: 'Choose Professional →',
    ctaHref: '/signup',
    featured: true,
  },
  {
    name: 'Enterprise',
    kicker: 'For complex shops',
    price: '$8,999',
    per: '/year',
    tagline:
      'For contractors, consultants, and enterprise teams managing up to two organizations.',
    features: [
      'Unlimited seats',
      'Up to two organization workspaces',
      'Everything in Professional',
      'Advanced analytics and reporting',
      'Priority support & assisted onboarding',
    ],
    cta: 'Talk to us →',
    ctaHref: 'mailto:support@grantrfpnavigator.com',
  },
];
